import React, { useContext } from "react";
import { Box } from "@mui/material";
import { ParagraphText, UBAppModal } from "../../../../Libraries";
import { LanguageContext } from "../../../../context/LanguageContext";
import styles from "./styles.module.css";
import { analytics, triggerLogEvent } from "../../../../config/firebase";

/**
 * VideoModal - unified modal for both use cases:
 * 1. Full mode (QuickSale): open, onClose, title, subtitle, videoSrc, primaryButton, secondaryButton
 * 2. Simple mode (ListItem): header, body{video, toggle, toggleVideoModal}, footer
 */
const VideoModal = ({
  open,
  onClose,
  title = "",
  subtitle = "",
  videoSrc,
  videoPoster,
  youtubeEmbedUrl,
  primaryButton = {},
  secondaryButton = {},
  className,
  header: headerProp,
  body,
  footer: footerProp,
}) => {
  const [I18n] = useContext(LanguageContext);

  const isSimpleMode = body?.video != null && body?.toggleVideoModal != null;

  const modalOpen = isSimpleMode ? !!body?.toggle : !!open;
  const modalOnClose = isSimpleMode ? () => body?.toggleVideoModal?.(false) : onClose;
  const videoUrl = isSimpleMode ? body?.video : videoSrc;

  const {
    text: primaryText = "Add Quick Sale",
    onClick: primaryOnClick,
    loading: primaryLoading = false,
    ...primaryRest
  } = primaryButton;

  const {
    text: secondaryText = "Close",
    onClick: secondaryOnClick,
    ...secondaryRest
  } = secondaryButton;

  const handleSecondaryClick = () => {
    triggerLogEvent(analytics, "video_modal_closed");
    secondaryOnClick?.();
    modalOnClose?.();
  };

  const handlePrimaryClick = () => {
    triggerLogEvent(analytics, "video_modal_cta_clicked");
    primaryOnClick?.();
    modalOnClose?.();
  };

  const modalHeader = isSimpleMode
    ? { title: headerProp?.title, handleClose: modalOnClose }
    : { handleClose: modalOnClose };

  const normalizedFooter = Array.isArray(footerProp)
    ? footerProp
    : footerProp?.title
    ? [
        { data: { title: secondaryText, onClick: handleSecondaryClick, id: "video_modal_close", className: styles.ubSecondaryButton } },
        { data: { title: footerProp.title, onClick: () => { footerProp.onClick?.(); modalOnClose?.(); }, id: "video_modal_footer_btn", className: styles.ubPrimaryButton } },
      ]
    : null;

  const modalFooter = normalizedFooter?.length
    ? normalizedFooter
    : isSimpleMode
    ? []
    : [
        {
          data: {
            onClick: handleSecondaryClick,
            title: secondaryText,
            id: "video_modal_close",
            className: styles.ubSecondaryButton,
            ...secondaryRest,
          },
        },
        {
          data: {
            onClick: handlePrimaryClick,
            title: primaryText,
            id: "video_modal_primary",
            loading: primaryLoading,
            className: styles.ubPrimaryButton,
            ...primaryRest,
          },
        },
      ];

  const renderVideoContent = () => {
    if (youtubeEmbedUrl) {
      return (
        <iframe
          src={youtubeEmbedUrl}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className={styles.youtubeEmbed}
        />
      );
    }
    if (videoUrl) {
      return (
        <video
          src={videoUrl}
          poster={videoPoster}
          controls
          className={styles.video}
        >
          Your browser does not support the video tag.
        </video>
      );
    }
    return (
      <Box className={styles.videoPlaceholder}>
        <ParagraphText className={styles.placeholderText}>
          Video preview
        </ParagraphText>
      </Box>
    );
  };

  const renderContent = () => {
    if (isSimpleMode) {
      return (
        <Box className={styles.videoPlayerOnly}>
          {youtubeEmbedUrl ? (
            <iframe
              src={youtubeEmbedUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className={styles.youtubeEmbed}
            />
          ) : (
            <video width="100%" controls src={videoUrl} className={styles.video}>
              Your browser does not support the video.
            </video>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Box className={styles.videoSection}>
          <Box className={styles.videoContainer}>
            {renderVideoContent()}
          </Box>
        </Box>

        {(title || subtitle) && (
          <Box className={styles.textSection}>
            {title && (
              <ParagraphText className={styles.title}>{title}</ParagraphText>
            )}
            {subtitle && (
              <ParagraphText className={styles.subtitle}>{subtitle}</ParagraphText>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <UBAppModal
      id="video_modal"
      open={modalOpen}
      className={`${styles.videoModal} ${isSimpleMode ? styles.videoPlayerModal : ""} ${className || ""}`}
      header={modalHeader}
      footer={modalFooter}
    >
      {renderContent()}
    </UBAppModal>
  );
};

export default VideoModal;
